import React, { useState, useEffect, useLayoutEffect, useRef } from "react";
import { Box, Typography, InputBase } from "@mui/material";
import { toPrice } from "../../../../../../helpers";

interface FeeRowProps {
  label: string;
  value: string;
  strikethrough?: boolean;
  editable?: boolean;
  onChange?: (value: number) => void;
}

export default function FeeRow({ 
  label, 
  value, 
  strikethrough,
  editable = false,
  onChange,
}: FeeRowProps) {
  const [isEditing, setIsEditing] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [inputWidth, setInputWidth] = useState(0);
  const [inputValue, setInputValue] = useState<number>(
    parseFloat(value.replace(/[^0-9.-]/g, ""))
  );
  const textRef = useRef<HTMLSpanElement>(null);
  const measureRef = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    setInputValue(parseFloat(value.replace(/[^0-9.-]/g, "")));
  }, [value]);

  // Capture the exact width of the displayed text before switching to edit mode
  const handleClick = () => {
    if (editable && textRef.current) {
      const exactWidth = textRef.current.offsetWidth;
      setInputWidth(exactWidth);
      setIsEditing(true);
    }
  };

  // Update width dynamically as user types
  useLayoutEffect(() => {
    if (isEditing && measureRef.current) {
      const textWidth = measureRef.current.offsetWidth;
      setInputWidth(Math.max(textWidth, 20));
    }
  }, [inputValue, isEditing]);

  return (
    <Box
      display="flex"
      justifyContent="space-between"
      alignItems="center"
      sx={{
        py: 0.125,
        minHeight: "20px",
        pr: "20px",
        textDecorationLine: strikethrough ? "line-through" : "none",
      }}
    >
      <Typography fontSize="12px" fontWeight={400} color="#555555" lineHeight="18px">
        {label}
      </Typography>
      
      {!isEditing ? (
        <Typography 
          ref={textRef}
          component="span"
          fontSize="12px" 
          fontWeight={400} 
          color="#555555" 
          lineHeight="18px"
          sx={{
            cursor: editable ? "pointer" : "default",
            textDecorationLine: editable ? "underline" : "none",
            textDecorationStyle: "dotted",
            textDecorationColor: "#555555",
            textUnderlineOffset: "2px",
          }}
          onClick={handleClick}
        >
          {value}
        </Typography>
      ) : (
        <InputBase
          type="number"
          value={isFocused ? inputValue : inputValue.toFixed(2)}
          onChange={(event) => {
            const newValue = toPrice(event.target.value);
            onChange?.(newValue);
            setInputValue(newValue);
          }}
          onBlur={() => {
            setIsFocused(false);
            setIsEditing(false);
          }}
          slotProps={{
            input: { 
              step: 0.01, 
              min: 0,
              onFocus: (e) => e.target.select(),
            },
          }}
          sx={{
            width: `${inputWidth}px`,
            fontSize: "12px",
            lineHeight: "18px",
            fontWeight: 400,
            color: "#555555",
            textAlign: "right",
            "& input": {
              textAlign: "right",
              padding: "0",
              fontSize: "12px",
              fontWeight: 400,
              "&::-webkit-outer-spin-button, &::-webkit-inner-spin-button": {
                WebkitAppearance: "none",
                margin: 0,
              },
              MozAppearance: "textfield",
            },
          }}
          autoFocus
          onFocus={(event) => {
            setIsFocused(true);
            event.target.select();
          }}
        />
      )}

      {/* Hidden span to measure text width while editing */}
      {isEditing && (
        <span
          ref={measureRef}
          style={{
            position: "absolute",
            visibility: "hidden",
            whiteSpace: "pre",
            fontSize: "12px",
            fontWeight: 400,
            fontFamily: "inherit",
            padding: 0,
            margin: 0,
            border: 0,
          }}
        >
          {isFocused ? inputValue : inputValue.toFixed(2)}
        </span>
      )}
    </Box>
  );
}

